"use client";

import type { createClient } from "@/lib/supabase/client";
import { resetAnalytics, trackLogout } from "@/lib/analytics";
import { clearTrialCheckoutSettled } from "@/lib/billing/trial-checkout-settled";

type SupabaseBrowserClient = ReturnType<typeof createClient>;

/**
 * Standard logout — ends ONLY the current device's session.
 *
 * `supabase.auth.signOut()` defaults to `scope: "global"`, which revokes EVERY
 * session and refresh token for the user — so logging out on desktop also
 * killed the same account's mobile session (the reported bug). Passing
 * `scope: "local"` removes only THIS device's session; other devices stay
 * signed in. Security-sensitive flows (password reset) and an explicit
 * "log out of all devices" action keep the global behaviour via
 * `signOutAllDevices()` below.
 *
 * Analytics are fired BEFORE `resetAnalytics()` because `posthog.reset()` clears
 * the distinct id — capturing after it would attribute the logout to an
 * anonymous user. Callers keep their own post-logout navigation / state reset.
 */
export async function signOutCurrentDevice(
  supabase: SupabaseBrowserClient,
): Promise<{ error: Error | null }> {
  trackLogout("logout_clicked", "current_session");
  const { error } = await supabase.auth.signOut({ scope: "local" });
  if (!error) trackLogout("session_invalidated", "current_session");
  trackLogout("logout_completed", "current_session", { error: error?.message });
  // Tab-scoped purchase state belongs to the session that made it. sessionStorage
  // outlives a sign-out, so without this the next account signed in to this SAME
  // tab would inherit "you already bought the trial" and have its own trial
  // checkout bounce silently — see lib/billing/trial-checkout-settled.ts.
  clearTrialCheckoutSettled();
  resetAnalytics();
  return { error };
}

/**
 * Global logout — ends EVERY session for the user (all devices). Reserved for
 * security-sensitive flows (password reset / forced invalidation) and an
 * explicit "log out of all devices" action; never the default per-device
 * logout. Use `scope: "others"` instead if a future flow needs to end all
 * OTHER sessions while keeping the current one signed in.
 *
 * Same analytics ordering rule as above: capture first, then `resetAnalytics()`
 * — this browser is signed out too, so it must not keep emitting events under
 * the old distinct id.
 */
export async function signOutAllDevices(
  supabase: SupabaseBrowserClient,
): Promise<{ error: Error | null }> {
  const { error } = await supabase.auth.signOut({ scope: "global" });
  if (!error) {
    trackLogout("session_invalidated", "all_sessions");
    trackLogout("global_logout_completed", "all_sessions");
  }
  // Tab-scoped purchase state belongs to the session that made it. sessionStorage
  // outlives a sign-out, so without this the next account signed in to this SAME
  // tab would inherit "you already bought the trial" and have its own trial
  // checkout bounce silently — see lib/billing/trial-checkout-settled.ts.
  clearTrialCheckoutSettled();
  resetAnalytics();
  return { error };
}
